// =============================================================================
// BrandingSettingsPage — Organization-level designer branding defaults
// =============================================================================
// Lets a merchant set the org-wide designer branding (logo + accent color) used
// across every connected store unless a store overrides it. Resolution
// precedence (store -> org -> platform default) lives in the API; this page
// only edits the ORG default level.
//
// Logo files are uploaded to a dedicated multipart endpoint that returns a
// relative storage key (the source of truth); the key is then persisted via the
// JSON branding PUT. Public URLs come back resolved for an immediate preview.
// =============================================================================

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardHeader,
  Input,
  Select,
  Spinner,
  useConfirm,
} from '@tkpod/ui';
import {
  hasAnyPermission,
  resolveDesignerBrandingDisplay,
  type DesignerBrandingInput,
  type DesignerBrandingStored,
  type DesignerLogoVariant,
  type ResolvedDesignerBranding,
} from '@tkpod/shared';
import { useAuth } from '@/hooks/use-auth';
import { api } from '@/lib/api';

/** API shape returned by GET /organizations/current/designer-branding. */
interface EditableBranding {
  stored: DesignerBrandingStored;
  resolved: ResolvedDesignerBranding;
}

type LogoSlot = 'primary' | 'light';

const VARIANT_OPTIONS: { value: DesignerLogoVariant; label: string }[] = [
  { value: 'auto', label: 'Auto (match background)' },
  { value: 'dark', label: 'Dark logo (for light backgrounds)' },
  { value: 'light', label: 'Light logo (for dark backgrounds)' },
];

const DEFAULT_ACCENT = '#2563eb';

const BRANDING_KEY = ['organization', 'designer-branding'] as const;
const LOGO_ENDPOINT = '/api/v1/organizations/current/designer-branding/logo';

/**
 * Upload a single logo file to the multipart endpoint and return its relative
 * storage key. Routes through the shared api client so cookie auth, CSRF
 * (X-CSRF-Token double-submit), refresh-on-401 and retry all apply — the client
 * sends FormData as multipart without forcing a JSON Content-Type.
 */
async function uploadLogoFile(file: File): Promise<string> {
  const form = new FormData();
  form.append('file', file);
  const json = await api.post<{ data: { key: string; url: string } }>(
    LOGO_ENDPOINT,
    form,
  );
  return json.data.key;
}

export function BrandingSettingsPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { confirm, dialog } = useConfirm();

  const permissions = user?.currentOrganization?.permissions ?? [];
  const canEdit = hasAnyPermission(permissions, ['settings:update', 'settings:manage']);

  const [accentColor, setAccentColor] = useState<string>('');
  const [buttonTextColor, setButtonTextColor] = useState<string>('');
  const [variant, setVariant] = useState<DesignerLogoVariant>('auto');
  const [accentError, setAccentError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [uploadingSlot, setUploadingSlot] = useState<LogoSlot | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const initializedRef = useRef(false);
  const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const primaryInputRef = useRef<HTMLInputElement>(null);
  const lightInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    };
  }, []);

  const brandingQuery = useQuery<EditableBranding>({
    queryKey: BRANDING_KEY,
    queryFn: () => api.get<EditableBranding>('/api/v1/organizations/current/designer-branding'),
    enabled: !!user?.currentOrganization,
  });

  useEffect(() => {
    if (brandingQuery.data && !initializedRef.current) {
      initializedRef.current = true;
      setAccentColor(brandingQuery.data.stored.accentColor ?? '');
      setButtonTextColor(brandingQuery.data.stored.buttonTextColor ?? '');
      setVariant(brandingQuery.data.stored.defaultLogoVariant ?? 'auto');
    }
  }, [brandingQuery.data]);

  const flashSuccess = (message: string) => {
    setSuccessMessage(message);
    if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    successTimeoutRef.current = setTimeout(() => setSuccessMessage(null), 3000);
  };

  const updateMutation = useMutation<EditableBranding, Error, DesignerBrandingInput>({
    mutationFn: (input) =>
      api.put<EditableBranding>('/api/v1/organizations/current/designer-branding', input),
    onSuccess: (data) => {
      queryClient.setQueryData(BRANDING_KEY, data);
      // Re-sync local fields from the canonical stored values.
      setAccentColor(data.stored.accentColor ?? '');
      setButtonTextColor(data.stored.buttonTextColor ?? '');
      setVariant(data.stored.defaultLogoVariant ?? 'auto');
    },
  });

  const handleSaveTheme = () => {
    setAccentError(null);
    setSuccessMessage(null);

    const trimmed = accentColor.trim();
    const trimmedText = buttonTextColor.trim();
    const hexRe = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
    if (trimmed && !hexRe.test(trimmed)) {
      setAccentError('Must be a hex color like #2563eb.');
      return;
    }
    if (trimmedText && !hexRe.test(trimmedText)) {
      setAccentError('Button text color must be a hex color like #ffffff.');
      return;
    }

    updateMutation.mutate(
      {
        accentColor: trimmed ? trimmed : null,
        buttonTextColor: trimmedText ? trimmedText : null,
        defaultLogoVariant: variant,
      },
      { onSuccess: () => flashSuccess('Branding theme saved.') },
    );
  };

  const handleLogoSelected = async (slot: LogoSlot, file: File | undefined) => {
    if (!file) return;
    setUploadError(null);
    setSuccessMessage(null);
    setUploadingSlot(slot);
    try {
      const key = await uploadLogoFile(file);
      await updateMutation.mutateAsync(
        slot === 'primary'
          ? { logoStorageKey: key }
          : { lightLogoStorageKey: key },
      );
      flashSuccess(slot === 'primary' ? 'Primary logo updated.' : 'Light logo updated.');
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Logo upload failed.');
    } finally {
      setUploadingSlot(null);
      if (slot === 'primary' && primaryInputRef.current) primaryInputRef.current.value = '';
      if (slot === 'light' && lightInputRef.current) lightInputRef.current.value = '';
    }
  };

  const handleClearLogo = async (slot: LogoSlot) => {
    const ok = await confirm({
      title: slot === 'primary' ? 'Remove primary logo' : 'Remove light logo',
      description:
        'The designer will fall back to the next available logo, then your store name.',
      confirmLabel: 'Remove',
      tone: 'danger',
    });
    if (!ok) return;

    setSuccessMessage(null);
    updateMutation.mutate(
      slot === 'primary' ? { logoStorageKey: null } : { lightLogoStorageKey: null },
      { onSuccess: () => flashSuccess('Logo removed.') },
    );
  };

  if (!user?.currentOrganization) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-text-tertiary">
        No organization found. Please contact support.
      </div>
    );
  }

  if (brandingQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (brandingQuery.isError || !brandingQuery.data) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="rounded-md bg-danger/10 px-4 py-3 text-sm text-danger">
          {brandingQuery.error instanceof Error
            ? brandingQuery.error.message
            : 'Failed to load branding settings. Please try again later.'}
        </div>
      </div>
    );
  }

  const { stored, resolved } = brandingQuery.data;
  const previewAccent = (accentColor.trim() || resolved.accentColor) ?? DEFAULT_ACCENT;
  const previewButtonText =
    (buttonTextColor.trim() || resolved.buttonTextColor) ?? '#ffffff';
  const display = resolveDesignerBrandingDisplay(resolved);

  return (
    <div className="space-y-6">
      {dialog}

      <Card>
        <CardHeader
          title="Designer Branding"
          description="Set the default logo and accent color shown in the designer for every connected store. Stores can override these in their own settings."
        />

        <div className="space-y-6 p-6 pt-2">
          {successMessage && (
            <div className="rounded-md bg-success/10 px-4 py-3 text-sm text-success">
              {successMessage}
            </div>
          )}
          {(updateMutation.error || uploadError) && (
            <div className="rounded-md bg-danger/10 px-4 py-3 text-sm text-danger">
              {uploadError ?? updateMutation.error?.message ?? 'Something went wrong.'}
            </div>
          )}
          {!canEdit && (
            <div className="rounded-md bg-warning/10 px-4 py-3 text-sm text-warning">
              You can view branding but need the “settings:update” permission to make changes.
            </div>
          )}

          {/* Live preview ----------------------------------------------------- */}
          <div>
            <p className="mb-2 text-sm font-medium text-text-primary">Designer top bar preview</p>
            <div className="flex items-center justify-between rounded-lg border border-border bg-surface-secondary px-4 py-3">
              <div className="flex items-center gap-3">
                {display.kind === 'logo' ? (
                  <img
                    src={display.url}
                    alt="Designer logo preview"
                    className="h-8 max-w-[160px] object-contain"
                  />
                ) : display.kind === 'text' ? (
                  <span className="text-base font-semibold text-text-primary">{display.label}</span>
                ) : (
                  <span className="text-base font-semibold text-text-tertiary">
                    Transfer Kingdom
                  </span>
                )}
              </div>
              <button
                type="button"
                disabled
                style={{ backgroundColor: previewAccent, color: previewButtonText }}
                className="rounded-md px-4 py-1.5 text-sm font-medium opacity-90"
              >
                Add to cart
              </button>
            </div>
            <p className="mt-1.5 text-xs text-text-tertiary">
              Effective source: <span className="font-medium">{resolved.source}</span>
            </p>
          </div>

          {/* Logos ------------------------------------------------------------ */}
          <div className="grid gap-6 sm:grid-cols-2">
            <LogoSlotEditor
              title="Primary logo"
              hint="Shown on light backgrounds. PNG, JPEG, WebP, or SVG up to 2 MB."
              url={resolved.logoUrl}
              hasOwnValue={!!stored.logoStorageKey}
              uploading={uploadingSlot === 'primary'}
              busy={updateMutation.isPending}
              canEdit={canEdit}
              inputRef={primaryInputRef}
              onSelect={(file) => void handleLogoSelected('primary', file)}
              onClear={() => void handleClearLogo('primary')}
            />
            <LogoSlotEditor
              title="Light logo"
              hint="Shown on dark backgrounds. Optional — falls back to the primary logo."
              url={resolved.lightLogoUrl}
              hasOwnValue={!!stored.lightLogoStorageKey}
              uploading={uploadingSlot === 'light'}
              busy={updateMutation.isPending}
              canEdit={canEdit}
              inputRef={lightInputRef}
              onSelect={(file) => void handleLogoSelected('light', file)}
              onClear={() => void handleClearLogo('light')}
            />
          </div>

          {/* Theme ------------------------------------------------------------ */}
          <div className="grid max-w-lg gap-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-text-primary">
                Accent color
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  aria-label="Accent color picker"
                  value={/^#[0-9a-fA-F]{6}$/.test(accentColor) ? accentColor : previewAccent}
                  disabled={!canEdit}
                  onChange={(e) => {
                    setAccentColor(e.target.value);
                    setAccentError(null);
                  }}
                  className="h-10 w-12 shrink-0 cursor-pointer rounded-md border border-border bg-surface p-1 disabled:cursor-not-allowed disabled:opacity-50"
                />
                <div className="flex-1">
                  <Input
                    value={accentColor}
                    placeholder={resolved.accentColor ?? DEFAULT_ACCENT}
                    disabled={!canEdit}
                    onChange={(e) => {
                      setAccentColor(e.target.value);
                      setAccentError(null);
                    }}
                    error={accentError ?? undefined}
                    hint="Hex like #2563eb. Leave blank to use the platform default."
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-text-primary">
                Button text color
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  aria-label="Button text color picker"
                  value={
                    /^#[0-9a-fA-F]{6}$/.test(buttonTextColor)
                      ? buttonTextColor
                      : resolved.buttonTextColor ?? '#ffffff'
                  }
                  disabled={!canEdit}
                  onChange={(e) => {
                    setButtonTextColor(e.target.value);
                    setAccentError(null);
                  }}
                  className="h-10 w-12 shrink-0 cursor-pointer rounded-md border border-border bg-surface p-1 disabled:cursor-not-allowed disabled:opacity-50"
                />
                <div className="flex-1">
                  <Input
                    value={buttonTextColor}
                    placeholder={resolved.buttonTextColor ?? '#ffffff'}
                    disabled={!canEdit}
                    onChange={(e) => {
                      setButtonTextColor(e.target.value);
                      setAccentError(null);
                    }}
                    hint="Label color on accent buttons. Leave blank for white (#ffffff)."
                  />
                </div>
              </div>
            </div>

            <Select
              label="Preferred logo variant"
              value={variant}
              disabled={!canEdit}
              onChange={(e) => setVariant(e.target.value as DesignerLogoVariant)}
              options={VARIANT_OPTIONS}
              hint="“Auto” picks the dark or light logo based on the surface background."
            />

            <div className="pt-1">
              <Button
                onClick={handleSaveTheme}
                disabled={!canEdit || updateMutation.isPending || uploadingSlot !== null}
                loading={updateMutation.isPending && uploadingSlot === null}
              >
                Save theme
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Logo slot editor (preview + upload + clear)
// ---------------------------------------------------------------------------

interface LogoSlotEditorProps {
  title: string;
  hint: string;
  url: string | null;
  hasOwnValue: boolean;
  uploading: boolean;
  busy: boolean;
  canEdit: boolean;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onSelect: (file: File | undefined) => void;
  onClear: () => void;
}

function LogoSlotEditor({
  title,
  hint,
  url,
  hasOwnValue,
  uploading,
  busy,
  canEdit,
  inputRef,
  onSelect,
  onClear,
}: LogoSlotEditorProps) {
  return (
    <div className="rounded-lg border border-border p-4">
      <p className="text-sm font-medium text-text-primary">{title}</p>
      <p className="mt-0.5 text-xs text-text-tertiary">{hint}</p>

      <div className="mt-3 flex h-20 items-center justify-center rounded-md border border-dashed border-border bg-surface-secondary">
        {uploading ? (
          <Spinner size="sm" />
        ) : url ? (
          <img src={url} alt={`${title} preview`} className="max-h-16 max-w-[80%] object-contain" />
        ) : (
          <span className="text-xs text-text-tertiary">No logo</span>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/svg+xml"
        className="hidden"
        onChange={(e) => onSelect(e.target.files?.[0])}
      />

      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={!canEdit || uploading || busy}
          onClick={() => inputRef.current?.click()}
        >
          {url ? 'Replace' : 'Upload'}
        </Button>
        {hasOwnValue && (
          <Button
            size="sm"
            variant="danger"
            disabled={!canEdit || uploading || busy}
            onClick={onClear}
          >
            Remove
          </Button>
        )}
      </div>
    </div>
  );
}
